const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let errBody: unknown = undefined;
    try { errBody = await res.json(); } catch { /* ignore */ }
    const msg = errBody && typeof errBody === 'object' && 'message' in errBody
      ? String((errBody as { message: unknown }).message)
      : res.statusText;
    throw new ApiError(res.status, msg, errBody);
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Nazwa pliku z nagłówka Content-Disposition. Wariant RFC 5987 (`filename*=UTF-8''…`) ma
 * pierwszeństwo, bo tylko on niesie polskie znaki; `filename="…"` jest fallbackiem ASCII.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* spadamy na wariant ASCII */ }
  }
  const plain = /filename="([^"]+)"|filename=([^;]+)/i.exec(header);
  const raw = plain ? (plain[1] ?? plain[2]) : null;
  return raw ? raw.trim() : null;
}

/**
 * POST zwracający plik — pobiera odpowiedź jako Blob i zapisuje ją u użytkownika.
 * Nazwa pliku pochodzi z Content-Disposition; przy API na innym originie nagłówek nie jest
 * safelisted w CORS, więc zostaje `fallbackName`.
 */
export async function downloadPost(path: string, body: unknown, fallbackName: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = await res.json();
      if (j && typeof j === 'object' && 'message' in j) msg = String((j as { message: unknown }).message);
    } catch { /* pusta odpowiedź błędu — zostaje statusText */ }
    throw new ApiError(res.status, msg);
  }
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Odroczone zwolnienie — część przeglądarek przerywa pobieranie przy natychmiastowym revoke.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
